import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { channels } from "@/mocks/channels";
import { site } from "@/mocks/site";
import SearchOverlay from "@/components/feature/SearchOverlay";

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [channelOpen, setChannelOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setChannelOpen(false);
  }, [location.pathname]);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap text-sm transition-colors ${
      isActive ? "font-semibold text-primary-700" : "text-foreground-700 hover:text-primary-700"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="bg-foreground-950 text-background-50">
        <div className="flex w-full items-center justify-between gap-3 px-4 py-2 text-xs md:px-6">
          <p className="flex items-center gap-2 truncate">
            <i className="ri-flashlight-fill text-accent-400" aria-hidden="true" />
            <span className="truncate">今日新增 3 篇档案 · 持续整理幕后内幕与隐秘事件</span>
          </p>
          <p className="hidden shrink-0 items-center gap-1 text-background-300 sm:flex">
            <i className="ri-calendar-line" aria-hidden="true" />
            更新至 {site.updatedAt}
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-between gap-4 px-4 py-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-eye-2-line text-lg" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <strong className="font-heading text-lg font-bold tracking-wide text-foreground-950">
              {site.name}
            </strong>
            <span className="mt-0.5 text-[11px] tracking-wider text-foreground-500">
              幕后内幕 · 深度档案
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden items-center gap-7 md:flex">
          <NavLink to="/" end className={navClass}>
            首页
          </NavLink>
          <div
            className="relative"
            onMouseEnter={() => setChannelOpen(true)}
            onMouseLeave={() => setChannelOpen(false)}
          >
            <button
              type="button"
              onClick={() => setChannelOpen((v) => !v)}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-700 transition-colors hover:text-primary-700"
              aria-expanded={channelOpen}
            >
              栏目导航
              <i
                className={`ri-arrow-down-s-line transition-transform ${channelOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>
            {channelOpen && (
              <div className="absolute left-1/2 top-full w-[420px] -translate-x-1/2 pt-3">
                <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2 animate-fade">
                  {channels.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/channel/${c.slug}`}
                      className="flex items-start gap-2.5 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className={`${c.icon} text-base`} aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-900">{c.name}</span>
                        <span className="mt-0.5 block truncate text-xs text-foreground-500">
                          {c.tagline}
                        </span>
                      </span>
                    </Link>
                  ))}
                  <Link
                    to="/channels"
                    className="col-span-2 mt-1 flex items-center justify-center gap-1 rounded-md bg-background-100 py-2 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-200"
                  >
                    查看全部栏目
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            )}
          </div>
          <NavLink to="/about" className={navClass}>
            关于我们
          </NavLink>
          <NavLink to="/faq" className={navClass}>
            常见问题
          </NavLink>
          <NavLink to="/contact" className={navClass}>
            联系我们
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex h-9 cursor-pointer items-center gap-2 rounded-md border border-background-300 px-3 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
            aria-label="打开搜索"
          >
            <i className="ri-search-line" aria-hidden="true" />
            <span className="hidden lg:inline">搜索档案</span>
          </button>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 md:hidden"
            aria-label="打开菜单"
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <nav aria-label="移动端导航" className="flex w-full flex-col px-4 py-2 md:px-6">
            <NavLink to="/" end className="border-b border-background-200 py-3 text-sm text-foreground-800">
              首页
            </NavLink>
            <span className="border-b border-background-200 py-3 text-sm font-medium text-foreground-500">
              栏目导航
            </span>
            <div className="grid grid-cols-2 gap-2 py-3">
              {channels.map((c) => (
                <Link
                  key={c.slug}
                  to={`/channel/${c.slug}`}
                  className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-700"
                >
                  <i className={c.icon} aria-hidden="true" />
                  {c.name}
                </Link>
              ))}
            </div>
            <NavLink to="/about" className="border-b border-background-200 py-3 text-sm text-foreground-800">
              关于我们
            </NavLink>
            <NavLink to="/faq" className="border-b border-background-200 py-3 text-sm text-foreground-800">
              常见问题
            </NavLink>
            <NavLink to="/contact" className="py-3 text-sm text-foreground-800">
              联系我们
            </NavLink>
          </nav>
        </div>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}